import { Link, useRouter } from '@tanstack/react-router';
import {
    Building2,
    ChevronDown,
    FileSignature,
    LogOut,
    Shield,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { OrganizationMembershipPickerContent } from '@apps/web/components/layout/organization_membership_picker';
import { ThemeToggleRow } from '@apps/web/components/layout/theme_toggle';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { toast } from '@apps/web/lib/app_notifications';
import {
    buildOrganizationMembershipOptions,
    formatOrganizationDisplayName,
} from '@apps/web/lib/organization_display';
import { cn } from '@apps/web/lib/utils';
import {
    updateMeTestingRole,
    useAdminOrganizationSubAccount,
    useOrganization,
    useOrgMemberProfiles,
    useTestingRoleSwitchAccess,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OrgMemberRole } from '@libraries/typescript/api_client';

type OrganizationMenuProps = {
    readonly triggerClassName?: string;
    readonly traceReason?: string;
};

type TestingRole = 'platform_admin' | OrgMemberRole;

const TESTING_ROLE_LABELS: Record<TestingRole, string> = {
    platform_admin: 'Platform Admin',
    admin: 'Admin',
    scheduler: 'Scheduler',
    restricted_scheduler: 'Restricted Scheduler',
};

const TESTING_ROLE_OPTIONS: Array<TestingRole> = [
    'platform_admin',
    'admin',
    'scheduler',
    'restricted_scheduler',
];

function getTestingRole(
    userIsPlatformAdmin: boolean,
    orgMemberRole: OrgMemberRole | null | undefined,
): TestingRole {
    if (userIsPlatformAdmin) {
        return 'platform_admin';
    }
    return orgMemberRole ?? 'scheduler';
}

export function OrganizationMenu({
    triggerClassName,
    traceReason = 'organization-menu',
}: OrganizationMenuProps) {
    const router = useRouter();
    const isMobile = useIsMobile();
    const { data: user } = useUser({
        reason: traceReason,
    });
    const organizationId = useOrganizationId();
    const { data: subAccount } = useAdminOrganizationSubAccount(
        user?.admin === true ? organizationId : null,
    );
    const { data: organization, isLoading: orgLoading } = useOrganization(
        organizationId,
        { reason: traceReason },
    );
    const { data: organizations } = useOrgMemberProfiles({
        reason: traceReason,
    });
    const {
        data: canSwitchTestingRole,
        mutate: mutateTestingRoleSwitchAccess,
    } = useTestingRoleSwitchAccess({ reason: traceReason });
    const { setOrganizationId } = useOrganizationContext();
    const [menuOpen, setMenuOpen] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [organizationSearch, setOrganizationSearch] = useState('');
    const [isUpdatingTestingRole, setIsUpdatingTestingRole] = useState(false);

    const currentOrganizationName = useMemo(() => {
        if (orgLoading) {
            return 'Loading…';
        }
        if (organization) {
            return formatOrganizationDisplayName(
                organization,
                user?.admin === true,
            );
        }
        return '';
    }, [organization, orgLoading, user?.admin]);

    const organizationOptions = useMemo(() => {
        if (!organizations || organizations.length === 0) {
            return [];
        }
        return buildOrganizationMembershipOptions(
            organizations,
            user?.admin === true,
        );
    }, [organizations, user?.admin]);

    const currentOrgMemberProfile = useMemo(() => {
        return organizations?.find(
            (profile) => profile.organization_id === organizationId,
        );
    }, [organizationId, organizations]);

    const testingRole = getTestingRole(
        user?.admin === true,
        currentOrgMemberProfile?.role,
    );
    const canUseTestingRoleSwitch =
        organizationId !== null &&
        currentOrgMemberProfile !== undefined &&
        canSwitchTestingRole === true;

    const updateTestingRole = async (role: TestingRole) => {
        if (
            organizationId === null ||
            role === testingRole ||
            isUpdatingTestingRole
        ) {
            return;
        }

        setIsUpdatingTestingRole(true);
        try {
            await updateMeTestingRole({
                organization_id: organizationId,
                role,
            });
        } catch (error) {
            setIsUpdatingTestingRole(false);
            toast.error('Role update failed', {
                description:
                    error instanceof Error
                        ? error.message
                        : 'Refresh and try again.',
                persistNotification: false,
            });
            return;
        }

        // The switch flips `user.admin` server-side, so every SWR cache
        // (including useSWRInfinite lists, which key-filter mutate skips),
        // the websocket's cached admin status, and the router loaders are
        // all stale. A reload is the only thing that resets all of them.
        window.location.reload();
    };

    const trigger = (
        <PopoverTrigger asChild={true}>
            <button
                type="button"
                className={cn(
                    'text-foreground hover:bg-accent inline-flex h-8 min-w-0 flex-1 cursor-pointer items-center rounded-md px-1 text-left text-[15px] font-semibold select-none focus:outline-none',
                    triggerClassName,
                )}
            >
                <span className="truncate">{currentOrganizationName}</span>
                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0 opacity-50" />
            </button>
        </PopoverTrigger>
    );

    return (
        <Popover
            open={menuOpen}
            onOpenChange={(open: boolean) => {
                setMenuOpen(open);
                if (open) {
                    setOrganizationSearch('');
                    void mutateTestingRoleSwitchAccess();
                }
            }}
        >
            {isMobile ? (
                trigger
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild={true}>{trigger}</TooltipTrigger>
                    <TooltipContent side="bottom" align="start">
                        {currentOrganizationName}
                    </TooltipContent>
                </Tooltip>
            )}
            <PopoverContent
                side="bottom"
                align="start"
                className="max-h-(--radix-popover-content-available-height) w-auto max-w-[min(600px,var(--radix-popover-content-available-width))] min-w-[240px] overflow-auto p-1"
                onOpenAutoFocus={(event) => {
                    if (searchInputRef.current) {
                        event.preventDefault();
                        searchInputRef.current.focus();
                    }
                }}
            >
                <div className="px-2 py-1.5 text-sm">
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm leading-none font-medium">
                            {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-muted-foreground text-xs leading-none">
                            {user?.email}
                        </p>
                        {currentOrganizationName !== '' && (
                            <p className="text-sm leading-snug font-medium break-words">
                                {currentOrganizationName}
                            </p>
                        )}
                    </div>
                </div>
                <div className="bg-border -mx-1 my-1 h-px" />
                <div>
                    {user?.admin === true && (
                        <button
                            type="button"
                            onClick={() => {
                                setMenuOpen(false);
                                if (organizationId === null) {
                                    void router.navigate({ to: '/admin' });
                                    return;
                                }
                                void router.navigate({
                                    to: '/admin/organizations/$organization-id',
                                    params: {
                                        'organization-id': organizationId,
                                    },
                                });
                            }}
                            className="hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm"
                        >
                            <Shield className="mr-2 h-4 w-4" />
                            <span>Admin panel</span>
                        </button>
                    )}
                    {user?.admin === true &&
                        subAccount?.account_id !== undefined && (
                            <button
                                type="button"
                                onClick={() => {
                                    setMenuOpen(false);
                                    void router.navigate({
                                        to: '/admin/accounts/$accountId',
                                        params: {
                                            accountId: subAccount.account_id,
                                        },
                                    });
                                }}
                                className="hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm"
                            >
                                <Building2 className="mr-2 h-4 w-4" />
                                <span>Open Core Account</span>
                            </button>
                        )}
                    {user?.admin !== true &&
                        user?.phoebe_role === 'contractor' && (
                            <button
                                type="button"
                                onClick={() => {
                                    setMenuOpen(false);
                                    void router.navigate({
                                        to: '/admin/sales/agreements',
                                    });
                                }}
                                className="hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm"
                            >
                                <FileSignature className="mr-2 h-4 w-4" />
                                <span>Agreements</span>
                            </button>
                        )}
                    {canUseTestingRoleSwitch && (
                        <div
                            className="px-2 py-1.5"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                        >
                            <label
                                htmlFor="testing-role-select"
                                className="mb-1 block text-xs font-medium"
                            >
                                Role
                            </label>
                            <Select
                                value={testingRole}
                                disabled={isUpdatingTestingRole}
                                onValueChange={(role) => {
                                    void updateTestingRole(role as TestingRole);
                                }}
                            >
                                <SelectTrigger
                                    id="testing-role-select"
                                    className="bg-background h-8 w-full min-w-[190px] text-sm"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TESTING_ROLE_OPTIONS.map((role) => (
                                        <SelectItem key={role} value={role}>
                                            {TESTING_ROLE_LABELS[role]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {(user?.admin === true || canUseTestingRoleSwitch) &&
                        organizationOptions.length > 0 && (
                            <div className="bg-border -mx-1 my-1 h-px" />
                        )}
                    {organizationOptions.length > 0 && (
                        <OrganizationMembershipPickerContent
                            options={organizationOptions}
                            selectedOrganizationId={organizationId}
                            searchValue={organizationSearch}
                            onSearchValueChange={setOrganizationSearch}
                            onSelectOrganization={(nextOrganizationId) => {
                                setOrganizationId(nextOrganizationId);
                                setMenuOpen(false);
                            }}
                            searchInputRef={searchInputRef}
                        />
                    )}
                </div>
                <ThemeToggleRow variant="menu" />
                <div className="bg-border -mx-1 my-1 h-px" />
                <Link
                    to="/sign-out"
                    className="hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm"
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                </Link>
            </PopoverContent>
        </Popover>
    );
}
